/**
 * Hook for automatic page view tracking for TG users
 * Only logs activity for subcontractor role users
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { logPageView } from '../services/tgActivity'
import type { TGPageName } from '../types/tgActivity'

export function usePageTracking(
  pageName: TGPageName,
  entityId?: string,
  metadata?: Record<string, unknown>
) {
  const location = useLocation()
  const { profile } = useAuth()
  const hasLoggedRef = useRef(false)
  const lastPathRef = useRef<string>('')

  useEffect(() => {
    // Only log for subcontractor role
    if (profile?.role !== 'subcontractor') {
      return
    }

    // Get subcontractor_id from profile
    // The profile.id is the subcontractor_id for TG users
    const subcontractorId = profile.id

    if (!subcontractorId) {
      return
    }

    // Prevent duplicate logging for same path
    const currentPath = location.pathname
    if (hasLoggedRef.current && lastPathRef.current === currentPath) {
      return
    }

    // Log the page view
    logPageView(
      subcontractorId,
      pageName,
      currentPath,
      entityId,
      metadata
    )

    hasLoggedRef.current = true
    lastPathRef.current = currentPath

  }, [profile?.id, profile?.role, pageName, location.pathname, entityId, metadata])

  // Reset when entityId changes (for detail pages)
  useEffect(() => {
    if (entityId) {
      hasLoggedRef.current = false
    }
  }, [entityId])
}
